/**
 * ComingSoonPage - Leak page for future capabilities
 * 
 * Purpose: Signal future direction without hype, dates, or monetization.
 * Discoverable via footer link, not promoted.
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Sparkles, Users, BookOpen, Radio, Shield, ArrowRight } from 'lucide-react';

export default function ComingSoonPage() {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const handleEarlyAccess = (e) => {
    e.preventDefault();
    if (email.trim()) {
      // For now, just show success. Can wire to backend later.
      console.log('[ComingSoon] Early access request:', email);
      setSubmitted(true);
    }
  };

  // Inline the form to avoid nested component issues
  const renderEarlyAccessForm = (variant = 'default') => {
    if (submitted) {
      return (
        <div className={`flex items-center gap-2 ${variant === 'footer' ? 'text-sm' : ''}`}>
          <Sparkles className="w-4 h-4 text-rose-400" />
          <span className="text-slate-300">You&apos;re on the list. We&apos;ll be in touch.</span>
        </div>
      );
    }

    return (
      <form onSubmit={handleEarlyAccess} className="flex flex-col sm:flex-row gap-3">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="your@email.com"
          className="flex-1 px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-slate-400 focus:outline-none focus:border-rose-400/50 focus:ring-1 focus:ring-rose-400/30"
          required
        />
        <button
          type="submit"
          className="px-6 py-3 rounded-xl bg-white/10 border border-white/20 text-white font-medium hover:bg-white/20 transition-colors flex items-center justify-center gap-2"
        >
          Request Early Access
          <ArrowRight className="w-4 h-4" />
        </button>
      </form>
    );
  };

  return (
    <div className="min-h-screen surface-page relative overflow-hidden">
      {/* Background Image Layer */}
      <div 
        className="absolute inset-0 bg-cover bg-center opacity-[0.08] dark:opacity-[0.08]"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1519681393784-d120267933ba?w=1920&q=80')`,
        }}
      />
      
      {/* Dark Overlay - only in dark mode */}
      <div className="absolute inset-0 bg-white/50 dark:bg-black/75" />
      
      {/* Content */}
      <div className="relative z-10">
        {/* Navigation */}
        <nav className="px-6 py-5">
          <Link 
            to="/"
            className="inline-flex items-center gap-2 text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white transition-colors text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Unity Love
          </Link>
        </nav>

        {/* Hero */}
        <section className="px-6 py-20 max-w-3xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl font-serif text-white mb-6 tracking-tight">
            Something deeper is forming.
          </h1>
          <p className="text-lg sm:text-xl text-slate-300 leading-relaxed mb-4">
            A calm space where people don&apos;t just meet —<br className="hidden sm:block" />
            they learn, build, and grow together.
          </p>
          <p className="text-sm text-slate-500 mb-10">
            No feeds. No noise. No performance.
          </p>
          <div className="max-w-md mx-auto">
            {renderEarlyAccessForm()}
          </div>
        </section>

        {/* What's Emerging */}
        <section className="px-6 py-16 max-w-3xl mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <Sparkles className="w-5 h-5 text-rose-400/70" />
            <h2 className="text-2xl font-serif text-white">More than connection.</h2>
          </div>
          <p className="text-slate-300 leading-relaxed mb-8">
            Unity Love is evolving into a place where connection becomes capability.
            Members will soon be able to gather in focused spaces, share real skills,
            and turn conversation into action — together.
          </p>
          <ul className="space-y-3 text-slate-400">
            <li className="flex items-start gap-3">
              <span className="text-rose-400/60 mt-1">•</span>
              <span>Learn directly from other members</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-rose-400/60 mt-1">•</span>
              <span>Collaborate in small, purpose-driven groups</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-rose-400/60 mt-1">•</span>
              <span>Share knowledge responsibly</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-rose-400/60 mt-1">•</span>
              <span>Build toward real-world outcomes</span>
            </li>
          </ul>
        </section>

        {/* Learning Spaces */}
        <section className="px-6 py-16 max-w-3xl mx-auto border-t border-white/5">
          <div className="flex items-center gap-3 mb-6">
            <BookOpen className="w-5 h-5 text-rose-400/70" />
            <h2 className="text-2xl font-serif text-white">Learning, without the influencer layer.</h2>
          </div>
          <p className="text-slate-300 leading-relaxed mb-6">
            These aren&apos;t courses.
          </p>
          <p className="text-slate-400 leading-relaxed mb-8">
            They&apos;re small learning circles — where teaching carries responsibility
            and participation carries weight.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-slate-500 text-sm">
            <div className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-rose-400/40" />
              Engineering & technology
            </div>
            <div className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-rose-400/40" />
              Farming & sustainability
            </div>
            <div className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-rose-400/40" />
              Financial literacy & cooperative economics
            </div>
            <div className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-rose-400/40" />
              Craft, creativity, and cultural knowledge
            </div>
          </div>
        </section>

        {/* Rooms & Streams */}
        <section className="px-6 py-16 max-w-3xl mx-auto border-t border-white/5">
          <div className="flex items-center gap-3 mb-6">
            <Radio className="w-5 h-5 text-rose-400/70" />
            <h2 className="text-2xl font-serif text-white">Presence over performance.</h2>
          </div>
          <p className="text-slate-300 leading-relaxed mb-6">
            Upcoming rooms and streams are designed for participation — not attention.
          </p>
          <div className="space-y-2 text-slate-400 mb-6">
            <p>No tipping culture.</p>
            <p>No follower counts.</p>
            <p>No pressure to entertain.</p>
          </div>
          <p className="text-sm text-slate-500 italic">
            Visibility comes from consistency, not charisma.
          </p>
        </section>

        {/* Why This Stays Different */}
        <section className="px-6 py-16 max-w-3xl mx-auto border-t border-white/5">
          <div className="flex items-center gap-3 mb-6">
            <Shield className="w-5 h-5 text-rose-400/70" />
            <h2 className="text-2xl font-serif text-white">Built to stay human.</h2>
          </div>
          <ul className="space-y-3 text-slate-400 mb-8">
            <li className="flex items-center gap-3">
              <span className="text-slate-600">×</span>
              No public directories
            </li>
            <li className="flex items-center gap-3">
              <span className="text-slate-600">×</span>
              No viral incentives
            </li>
            <li className="flex items-center gap-3">
              <span className="text-slate-600">×</span>
              No creator grind
            </li>
            <li className="flex items-center gap-3">
              <span className="text-slate-600">×</span>
              No algorithmic pressure
            </li>
          </ul>
          <p className="text-slate-300">
            Growth happens through trust — not exposure.
          </p>
        </section>

        {/* Long View */}
        <section className="px-6 py-16 max-w-3xl mx-auto border-t border-white/5">
          <div className="flex items-center gap-3 mb-6">
            <Users className="w-5 h-5 text-rose-400/70" />
            <h2 className="text-2xl font-serif text-white">This is only the beginning.</h2>
          </div>
          <p className="text-slate-300 leading-relaxed mb-6">
            Over time, Unity Love will support action groups, shared projects,
            and cultural memory that outlives any single feature.
          </p>
          <div className="space-y-1 text-slate-500 text-sm">
            <p>Not fast.</p>
            <p>Not loud.</p>
            <p>Built to last.</p>
          </div>
        </section>

        {/* Footer */}
        <footer className="px-6 py-16 max-w-3xl mx-auto border-t border-white/5">
          <p className="text-slate-500 text-sm text-center mb-8">
            Built for intentional connection — not public feeds.
          </p>
          <div className="max-w-md mx-auto">
            {renderEarlyAccessForm('footer')}
          </div>
          <div className="mt-12 text-center">
            <Link 
              to="/"
              className="text-slate-600 hover:text-slate-400 transition-colors text-sm"
            >
              ← Return to Unity Love
            </Link>
          </div>
        </footer>
      </div>
    </div>
  );
}
